import { useEffect, useRef, useState } from "react";
import { useSiteLogo } from "@/lib/useSiteLogo";
import { dedupeHead } from "@/lib/seoMeta";
import { useNavigate, useParams, Link, useNavigationType } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import ArticleGallery, { splitImages, uniqueMedia, mediaKey } from "@/components/ArticleGallery";
import BreakingBadge from "@/components/BreakingBadge";
import { considerBreaking, useActiveBreakingId } from "@/lib/breaking";
import { useLang, OG_LOCALES, prefixFor } from "@/lib/i18n";
import { NewsCard } from "@/components/NewsCard";
import ShareButtons from "@/components/ShareButtons";
import SaveButton from "@/components/SaveButton";
import FontSizeControl, { useFontSize, FONT_SCALE } from "@/components/FontSizeControl";
import { sendReaderEvent, reportMediaError, trackScrollProgress } from "@/lib/readerEvents";
import { sendQualifiedView, watchArticleView } from "@/lib/views";
import { siteName } from "@/lib/siteIdentity";
import ArticleReelPlayer from "@/components/reels/ArticleReelPlayer";
import { Clock, User, Tag } from "lucide-react";
import { sectionLabel, shouldShowSectionBadge } from "@/lib/sections";
import { pickBody, stripLeadingTitle, stripInvisible, cleanDisplayText } from "@/lib/articleText";
import { gazaDateTimeArticle } from "@/lib/dateGaza";

// Single reading container spec — applied to every block inside the article
// page so short and long stories share the exact same rhythm and width.
const READER = {
    width: "calc(100% - 32px)",
    maxWidth: "800px",
    marginInline: "auto",
    boxSizing: "border-box",
};

const TEXT_WRAP = {
    whiteSpace: "normal",
    overflowWrap: "break-word",
    wordBreak: "normal",
    textAlign: "right",
    boxSizing: "border-box",
};

export default function ArticlePage() {
    const { slug } = useParams();
    const navType = useNavigationType();
    const [data, setData] = useState(null);
    const activeBreakingId = useActiveBreakingId();
    const { p, lang, t } = useLang();
    const navigate = useNavigate();
    const [errorState, setErrorState] = useState(null); // null | "gone" | "not_found" | "unavailable"
    const [fontSize, setFontSize] = useFontSize();
    const siteLogo = useSiteLogo();
    const contentRef = useRef(null);
    const endRef = useRef(null);
    const articleId = data?.article?.id;

    // «الوصول إلى نهاية نص الخبر»: once per page view when the end-of-text marker becomes visible (server dedupes per session).
    useEffect(() => {
        const el = endRef.current;
        if (!el || !articleId || typeof IntersectionObserver === "undefined") return;
        let sent = false;
        const io = new IntersectionObserver((entries) => {
            if (sent || !entries.some((e) => e.isIntersecting)) return;
            sent = true; io.disconnect();
            sendReaderEvent("read_end", { articleId, slug, lang });
        }, { threshold: 0.1 });
        io.observe(el);
        return () => io.disconnect();
    }, [articleId, slug, lang]);

    // «تقدّم التمرير» inside the article text (max %, never «read fully»).
    useEffect(() => {
        if (!articleId) return;
        const el = document.querySelector('[data-testid="article-content"]');
        return trackScrollProgress(el, { articleId, slug });
    }, [articleId, slug]);

    // Qualified article view: 5 s of the story on screen in a visible tab (server dedupes 30 min per viewer).
    useEffect(() => {
        if (!articleId) return;
        const el = document.querySelector('[data-testid="article-root"]');
        return watchArticleView(el, (sec) => sendQualifiedView(articleId, "article", sec));
    }, [articleId]);

    // Images inside the HTML body: report real load failures (grouped + capped in reportMediaError).
    useEffect(() => {
        const root = contentRef.current;
        if (!root) return;
        const onErr = (e) => {
            const img = e.target;
            if (img?.tagName !== "IMG") return;
            reportMediaError({ slug, url: img.currentSrc || img.src, kind: "image", errorKind: navigator.onLine === false ? "network_offline" : "load_failed" });
        };
        root.addEventListener("error", onErr, true);
        return () => root.removeEventListener("error", onErr, true);
    }, [slug, data]);

    useEffect(() => {
        if (navType !== "POP") window.scrollTo(0, 0);
        setErrorState(null);
        api.get(`/public/articles/${slug}`)
            .then((r) => {
                if (r.data?.redirect_slug) { navigate(`${p}/article/${r.data.redirect_slug}`, { replace: true }); return; }   // merged duplicate → canonical story
                setData(r.data); considerBreaking([r.data?.article]);
            })
            .catch((err) => {
                const code = err?.response?.status;
                if (code === 410) setErrorState("gone");
                else if (code === 404) setErrorState("not_found");
                else setErrorState("unavailable"); // transient (429/5xx/network) — never noindex
            });
    }, [slug, lang]);

    // noindex ONLY for definitive 404/410. Transient failures must not tag a
    // published article as noindex (a prerender snapshot would keep it).
    useEffect(() => {
        if (!errorState) return;
        const setMeta = (name, value) => {
            let el = document.querySelector(`meta[name="${name}"]`);
            if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
            el.setAttribute("content", value);
        };
        if (errorState !== "unavailable") { setMeta("robots", "noindex, nofollow"); setMeta("prerender-status-code", errorState === "gone" ? "410" : "404"); }   // pre-render layers return the real HTTP status
        document.title = errorState === "gone"
            ? `هذا الخبر لم يعد متاحًا | ${siteName("ar")}`
            : errorState === "unavailable"
                ? `الخبر غير متاح مؤقتًا | ${siteName("ar")}`
                : `الخبر غير موجود | ${siteName("ar")}`;
        return () => {
            document.querySelector('meta[name="robots"]')?.remove();
            document.querySelector('meta[name="prerender-status-code"]')?.remove();
        };
    }, [errorState]);

    useEffect(() => {
        if (!data?.article) return;
        const a = data.article;
        const cleanTitle = cleanDisplayText(a.title || "");
        const description = (cleanDisplayText(a.excerpt || a.content || "") || cleanTitle).slice(0, 300);
        // Generic one-word headlines ("عاجل"/"Urgent") are not unique page titles: add the lead sentence for <title>/og:title only.
        const lead = description.replace(/\s+/g, " ").slice(0, 80).replace(/\s+\S*$/, "");
        const pageTitle = cleanTitle.length < 12 && lead && lead !== cleanTitle ? `${cleanTitle}: ${lead}` : cleanTitle;
        window.prerenderReady = true;   // crawler pre-render layers: the story is in the DOM now
        const url = window.location.href;
        const image = a.cover_image ? fileUrl(a.cover_image) : (window.__SITE_LOGO__ || "");

        document.title = `${pageTitle} | ${t("siteName")}`;

        const setMeta = (attr, key, value) => {
            let el = document.querySelector(`meta[${attr}="${key}"]`);
            if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
            el.setAttribute("content", value);
        };
        setMeta("name",     "description",     description);
        setMeta("property", "og:title",        pageTitle);
        setMeta("property", "og:description",  description);
        setMeta("property", "og:type",         "article");
        setMeta("property", "og:url",          url);
        setMeta("property", "og:locale",       OG_LOCALES[a.lang] || "ar_AR");
        setMeta("property", "og:locale:alternate", a.lang !== "ar" ? "ar_AR" : "en_US");
        setMeta("property", "og:site_name",    siteName(a.lang));
        dedupeHead();
        if (image) setMeta("property", "og:image", image);
        setMeta("name",     "twitter:card",    image ? "summary_large_image" : "summary");
        setMeta("name",     "twitter:title",   pageTitle);
        setMeta("name",     "twitter:description", description);
        if (image) setMeta("name", "twitter:image", image);

        // Canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
        // hreflang: Arabic original + English version (only when a complete translation exists).
        // An /en/ page still showing Arabic is NOT a separate document: it canonicalises to the Arabic URL.
        const origin = window.location.origin;
        const aboutUrl = `${origin}${prefixFor(a.lang)}/about`;
        const arHref = `${origin}/article/${a.slug}`;
        const hasEn = !!a.translation?.available;
        canonical.setAttribute("href", a.lang !== "ar" && !hasEn ? arHref : url);
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
        const alts = [["ar", arHref], ["x-default", arHref]];
        for (const L of (a.translation?.languages || []).filter((x) => x !== "ar")) alts.push([L, `${origin}/${L}/article/${a.slug}`]);
        for (const [hl, href] of alts) { const l = document.createElement("link"); l.rel = "alternate"; l.hreflang = hl; l.href = href; document.head.appendChild(l); }
        if (a.lang !== "ar" && !a.translation?.available) canonical.setAttribute("href", arHref);  // incomplete EN page → canonical to the Arabic original

        // JSON-LD NewsArticle
        const ld = {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "headline": cleanTitle,
            "description": description,
            "datePublished": a.published_at || a.created_at,
            "dateModified": a.updated_at || a.published_at || a.created_at,
            "mainEntityOfPage": url,
            "url": url,
            "inLanguage": a.lang || "ar",
            "author": { "@type": "Person", "name": (a.lang !== "ar" && (!a.editor_name || a.editor_name.trim() === "علي سالم")) ? "Ali Salem" : (a.editor_name || "علي سالم"), "url": aboutUrl },
            "publisher": {
                "@type": "Organization",
                "name": siteName(a.lang),
                "url": aboutUrl,
                "logo": { "@type": "ImageObject", "url": window.location.origin + "/api/public/og-image.png", "width": 1200, "height": 630 }
            },
        };
        if (image) ld.image = [image];
        if (a.video_url && a.video_web_url) {   // the story's video, with its own indexable watch page
            const vPath = `${prefixFor(a.lang)}/video/${a.slug}`;
            ld.video = {
                "@type": "VideoObject", "name": cleanTitle, "description": description,
                "thumbnailUrl": [fileUrl(a.video_poster_url || a.cover_image || "/api/public/og-image.png")],
                "uploadDate": a.published_at || a.created_at, "contentUrl": fileUrl(a.video_web_url), "embedUrl": origin + vPath, "url": origin + vPath,
                "inLanguage": a.lang || "ar",
                "author": { "@type": "Person", "name": a.lang !== "ar" ? "Ali Salem" : "علي سالم", "url": aboutUrl },
                "publisher": { "@type": "Organization", "name": siteName(a.lang), "url": aboutUrl, "logo": { "@type": "ImageObject", "url": origin + "/api/public/og-image.png", "width": 1200, "height": 630 } },
                ...(a.video_duration ? { "duration": `PT${Math.round(a.video_duration)}S` } : {}),
            };
        }
        let ldEl = document.getElementById("ld-newsarticle");
        if (!ldEl) { ldEl = document.createElement("script"); ldEl.type = "application/ld+json"; ldEl.id = "ld-newsarticle"; document.head.appendChild(ldEl); }
        ldEl.textContent = JSON.stringify(ld);

        return () => {
            // Clean up the JSON-LD when leaving this page so it never leaks
            // between articles.
            const el = document.getElementById("ld-newsarticle");
            if (el) el.remove();
        };
    }, [data]);

    if (errorState) {
        const isGone = errorState === "gone";
        const isTransient = errorState === "unavailable";
        return (
            <div
                className="mx-auto py-16 text-center"
                style={{ width: "calc(100% - 32px)", maxWidth: "640px" }}
                data-testid={isGone ? "article-gone" : isTransient ? "article-unavailable" : "article-not-found"}
            >
                <div className="font-display font-black text-5xl text-brand-red mb-3" dir="ltr" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {isGone ? "410" : isTransient ? "…" : "404"}
                </div>
                <h1 className="font-display font-black text-2xl md:text-3xl text-brand-black dark:text-white mb-3">
                    {isGone ? t("gone") : isTransient ? t("transient") : t("notFound")}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    {isGone ? t("goneDesc") : isTransient ? t("transientDesc") : t("notFoundDesc")}
                </p>
                <Link
                    to={p || "/"}
                    className="inline-block bg-brand-red text-white px-5 py-2.5 rounded-sm text-sm font-bold hover:bg-brand-red-dark"
                    data-testid="article-back-home"
                >{t("backHome")}</Link>
            </div>
        );
    }
    if (!data) return (
        <div className="mx-auto py-8" data-testid="article-loading" style={{ width: "calc(100% - 32px)", maxWidth: "800px" }}>
            <div className="h-8 w-40 bg-gray-100 dark:bg-gray-800 rounded-sm mb-4 animate-pulse" />
            <div className="h-12 w-11/12 bg-gray-100 dark:bg-gray-800 rounded-sm mb-3 animate-pulse" />
            <div className="h-6 w-3/4 bg-gray-100 dark:bg-gray-800 rounded-sm mb-8 animate-pulse" />
            <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-sm mb-6 animate-pulse" />
            <div className="space-y-3">
                <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
                <div className="h-4 w-5/6 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
                <div className="h-4 w-4/6 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
            </div>
        </div>
    );

    const a = data.article;
    // Clean invisible glyphs from every user-facing value.
    const cleanTitle = stripInvisible(a.title || "");
    const cleanContent = stripInvisible(a.content || "");
    const cleanExcerpt = stripInvisible(a.excerpt || "");
    const cleanArticle = { ...a, title: cleanTitle, content: cleanContent, excerpt: cleanExcerpt };
    const url = window.location.href;

    // Unified body selection — the same helper used by the widget so the two
    // surfaces can never disagree. We render ONE body block and never show
    // excerpt as a separate paragraph.
    const isEnText = lang !== "ar" && !!a.translation?.available;
    const tdir = isEnText ? "ltr" : "rtl";
    const editorName = (lang !== "ar" && (!a.editor_name || a.editor_name.trim() === "علي سالم")) ? t("author") : (a.editor_name || "علي سالم").trim();
    const chosen = pickBody(cleanArticle);
    const rawBody = chosen.text || "";
    const bodyIsHtml = chosen.isHtml;
    const bodyStripped = stripLeadingTitle(rawBody, cleanTitle, bodyIsHtml);
    // Images live in ONE ordered gallery (cover first, distinct files only) — never inline giants.
    const { text: bodyClean, images: bodyImages } = bodyIsHtml ? splitImages(bodyStripped) : { text: bodyStripped, images: [] };
    // Cover first, then body images; the same file is never shown twice (any URL form).
    // Server-built unique gallery (sha256 identity: same file under another name counts once). Falls back to URL-level dedupe.
    const serverGallery = Array.isArray(a.gallery) && a.gallery.length ? uniqueMedia(a.gallery) : null;
    const galleryImages = serverGallery || uniqueMedia([cleanArticle.cover_image, ...bodyImages]);
    // With a video, the cover is its poster — only body images that are not the cover get a gallery.
    const extraImages = a.video_url ? galleryImages.filter((u) => mediaKey(u) !== mediaKey(cleanArticle.cover_image)) : [];
    const hasBody = !!(bodyClean && bodyClean.replace(/<[^>]+>/g, "").trim());

    return (
        <article dir={tdir} className="article-page py-6 md:py-8" data-testid="article-root">
            {/* Cover / video — the media itself sits inside the same reading rail.
              When the article has a video_url we skip the standalone cover
              here because the cover is already used as the video poster below,
              which prevents a duplicate image above the player. */}
            {galleryImages.length > 0 && !a.video_url && (
                <div style={READER}>
                    <ArticleGallery images={galleryImages} title={cleanTitle} slug={slug} />
                </div>
            )}

            {!a.video_url && a.media_status === "failed" && (
                <div style={READER} className="my-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 text-sm text-gray-600 dark:text-gray-300" data-testid="article-video-unavailable">
                    {t("videoUnavailable")}
                </div>
            )}
            {a.video_url && (
                <div style={READER} className="mb-5" data-testid="article-video-wrap">
                    <ArticleReelPlayer article={{ ...cleanArticle, slug }} />
                </div>
            )}
            {a.video_url && extraImages.length > 0 && (
                <div style={READER}><ArticleGallery images={extraImages} title={cleanTitle} slug={slug} /></div>
            )}

            {/* Section badge + breaking flag (Palestine badge is suppressed) */}
            <div style={READER} className="mb-4 flex flex-wrap items-center gap-2 text-xs">
                {shouldShowSectionBadge(a.section_slug) && (
                    <Link
                        to={`/section/${a.section_slug}`}
                        className="bg-brand-red text-white px-3 py-1 rounded-sm font-bold"
                        data-testid="article-section-badge"
                    >{sectionLabel(a.section_slug, lang)}</Link>
                )}
                {a.id === activeBreakingId && <BreakingBadge active testId="article-breaking-badge" />}
            </div>

            {/* Title */}
            <h1
                style={READER}
                className="news-title news-title--article text-brand-black dark:text-gray-100"
                dir={tdir}
                data-testid="article-title"
            >{cleanTitle}</h1>

            {lang !== "ar" && a.translation && (
                <div style={READER} className="translation-note" dir="ltr" data-testid="translation-note">
                    {a.translation.available ? (a.translation.status === "reviewed" ? null : t("machineNote")) : t("notTranslated")}{" "}
                    {a.translation.available && a.translation.stale ? t("staleNote") + " " : ""}
                    <Link to={`/article/${a.slug}`} data-testid="read-original">{t("readOriginal")}</Link>
                </div>
            )}

            {/* Unified body — a single block that combines content/excerpt so
              the story is never split, duplicated or mis-ordered. */}
            {hasBody && <FontSizeControl size={fontSize} onChange={setFontSize} style={READER} />}
            {hasBody && (
                bodyIsHtml ? (
                    <div
                        ref={contentRef}
                        className="prose-ar mt-4"
                        data-testid="article-content"
                        data-font-size={fontSize}
                        dir={tdir}
                        style={{ ...READER, ...TEXT_WRAP, lineHeight: 1.9, fontSize: `${FONT_SCALE[fontSize]}em` }}
                        dangerouslySetInnerHTML={{ __html: bodyClean }}
                    />
                ) : (
                    <div
                        ref={contentRef}
                        className="prose-ar mt-4"
                        data-testid="article-content"
                        data-font-size={fontSize}
                        dir={tdir}
                        style={{ ...READER, ...TEXT_WRAP, lineHeight: 1.9, fontSize: `${FONT_SCALE[fontSize]}em` }}
                    >
                        {bodyClean.split(/\n{2,}|\r?\n/).map((p, i) => p.trim() ? <p key={i} style={TEXT_WRAP}>{p}</p> : null)}
                    </div>
                )
            )}
            <div ref={endRef} aria-hidden="true" style={{ height: 1 }} data-testid="article-end-marker" />

            {data.updates?.length > 0 && (
                <section style={READER} className="mt-10 border-t border-gray-200 dark:border-gray-800 pt-6" data-testid="story-updates">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="h-6 w-1.5 rounded-sm bg-brand-red" />
                        <h2 className="font-display font-black text-xl text-brand-black dark:text-gray-100">{t("storyUpdates")}</h2>
                        <span className="text-[11px] font-bold bg-brand-red/10 text-brand-red px-2 py-1 rounded-sm">{t("updatesCount", data.updates.length)}</span>
                    </div>
                    <ol className="relative border-r-2 border-gray-200 dark:border-gray-700 pe-4 ps-2 space-y-4">
                        {data.updates.map((u) => (
                            <li key={u.id} className="relative">
                                <span className="absolute -right-[7px] top-2 h-3 w-3 rounded-full bg-brand-red" aria-hidden="true" />
                                <div className="text-[12px] font-bold text-brand-red" dir="ltr">{new Date(u.published_at || u.created_at).toLocaleString("ar-EG-u-nu-latn", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", hour12: true })}</div>
                                <Link to={`${p}/article/${u.slug}`} className="block mt-1 text-brand-black dark:text-gray-100 hover:text-brand-red text-[15px] leading-[1.85]">
                                    {stripInvisible(u.title || "")}
                                </Link>
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {a.tags?.length > 0 && (
                <div style={READER} className="mt-8 flex flex-wrap gap-2 items-center">
                    <Tag className="w-4 h-4 text-brand-red" />
                    {a.tags.map((t) => (
                        <Link key={t} to={`${p}/search?q=${encodeURIComponent(t)}`} className="text-xs bg-gray-100 dark:bg-gray-800 dark:text-gray-200 hover:bg-brand-red hover:text-white px-3 py-1 rounded-sm">{t}</Link>
                    ))}
                </div>
            )}

            {/* Byline + publish data — once, after the full body and before sharing */}
            <div style={{ ...READER, ...TEXT_WRAP }} className="article-meta mt-8 pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-500 dark:text-gray-400" dir={lang !== "ar" ? "ltr" : "rtl"} data-testid="article-meta">
                {editorName && (
                    <span className="flex items-center gap-1" data-testid="article-author-byline">
                        {siteLogo ? <img src={fileUrl(siteLogo)} alt="" className="w-6 h-6 rounded-full object-cover border border-gray-200 dark:border-gray-700 shrink-0" data-testid="article-author-photo" /> : <User className="w-4 h-4 text-brand-red shrink-0" aria-hidden="true" />}
                        <span className="font-medium">{editorName}</span>
                    </span>
                )}
                <span className="flex items-center gap-1" data-testid="article-date">
                    <Clock className="w-4 h-4 text-brand-red shrink-0" aria-hidden="true" />
                    <time dateTime={a.published_at || a.created_at}>{gazaDateTimeArticle(a.published_at || a.created_at, lang)}</time>
                </span>
                {/* Views are still counted server-side and shown in the admin panel only. */}
            </div>

            <div style={READER} className="mt-8 border-t border-gray-200 dark:border-gray-800 pt-6">
                <div className="font-display font-bold mb-3 dark:text-gray-100">{t("share")}</div>
                <div className="flex flex-wrap items-center gap-2">
                    <ShareButtons url={url} title={a.title} articleId={a.id} slug={a.slug} />
                    <SaveButton article={a} />
                </div>
            </div>

            {data.related?.length > 0 && (
                <section style={READER} className="mt-14">
                    <div className="flex items-center justify-between gap-3 mb-6 border-b border-gray-200 dark:border-gray-800 pb-3">
                        <div className="flex items-center gap-3">
                            <div className="h-8 w-1.5 rounded-sm bg-brand-red" />
                            <h2 className="font-display font-black text-2xl text-brand-black dark:text-gray-100">{t("related")}</h2>
                        </div>
                    </div>
                    <div className="news-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="related-grid">
                        {data.related.map((r) => <NewsCard key={r.id} article={r} />)}
                    </div>
                </section>
            )}
        </article>
    );
}
